import { useSiteContent } from '@/context/SiteContent';
import { Wordmark } from '@/components/yunti/Wordmark';

const VARIANTS = {
  light: {
    article:
      'p-8 md:p-10 bg-white border border-yunti-ink/10 flex flex-col justify-between min-h-[260px] relative overflow-hidden group hover:border-yunti-purple transition-colors',
    meta: 'text-yunti-ink/40',
    first: 'text-2xl font-display tracking-tight text-yunti-ink leading-snug',
    second: 'text-lg text-yunti-green font-mono',
    rule: 'border-yunti-ink/5',
    foot: 'text-yunti-ink/50',
  },
  dark: {
    article:
      'p-8 md:p-10 bg-yunti-ink text-yunti-ground border border-yunti-ink flex flex-col justify-between min-h-[260px] relative overflow-hidden group',
    meta: 'text-yunti-ground/50',
    first: 'text-2xl md:text-3xl font-display tracking-tight text-yunti-ground leading-snug',
    second: 'text-xl text-yunti-purplelight font-semibold italic',
    rule: 'border-white/10',
    foot: 'text-yunti-ground/50',
  },
  green: {
    article:
      'p-8 md:p-10 bg-yunti-green/10 border border-yunti-green/30 flex flex-col justify-between min-h-[260px] relative overflow-hidden group hover:border-yunti-green transition-colors',
    meta: 'text-yunti-green',
    first: 'text-2xl font-display tracking-tight text-yunti-ink leading-snug',
    second: 'text-lg text-yunti-green font-medium font-sans',
    rule: 'border-yunti-green/20',
    foot: 'text-yunti-green/70',
  },
  purple: {
    article:
      'p-8 md:p-10 bg-white border-2 border-yunti-purple/40 flex flex-col justify-between min-h-[260px] relative overflow-hidden group hover:border-yunti-purple transition-colors',
    meta: 'text-yunti-purple',
    first: 'text-2xl font-display tracking-tight text-yunti-ink leading-snug',
    second: 'text-2xl font-display uppercase tracking-widest text-yunti-purple',
    rule: 'border-yunti-ink/5',
    foot: 'text-yunti-ink/50',
  },
};

function PieceCard({ piece }) {
  const v = VARIANTS[piece.variant] || VARIANTS.light;
  return (
    <article className={v.article} data-testid={`${piece.id}-card`}>
      <div className={`flex justify-between items-start text-xs font-mono ${v.meta} mb-6`}>
        <span>{piece.label}</span>
        <span className={piece.variant === 'light' ? 'text-yunti-purple uppercase font-semibold' : 'uppercase font-semibold'}>
          {piece.tag}
        </span>
      </div>
      <blockquote className="space-y-3">
        <p className={v.first}>
          <Wordmark text={piece.line1} />
        </p>
        <p className={v.second}>
          <Wordmark text={piece.line2} />
        </p>
      </blockquote>
      <div className={`mt-8 pt-4 border-t ${v.rule} text-[11px] font-mono ${v.foot} tracking-wider`}>{piece.footnote}</div>
    </article>
  );
}

export function Perspective() {
  const { perspective } = useSiteContent();
  return (
    <section
      id="perspective"
      className="section-pad px-6 lg:px-12 bg-yunti-sand text-yunti-ink border-b border-yunti-ink/10"
      data-testid="perspective-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between pb-12 border-b border-yunti-ink/15 gap-6">
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.22em] text-yunti-green font-semibold block mb-2">
              {perspective.eyebrow}
            </span>
            <h2 className="text-4xl md:text-6xl font-display uppercase tracking-tight text-yunti-ink">{perspective.title}</h2>
          </div>
          <p className="text-sm md:text-base text-yunti-ink/70 max-w-md font-medium">{perspective.support}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          {perspective.pieces.map((piece) => (
            <PieceCard key={piece.id} piece={piece} />
          ))}
        </div>
      </div>
    </section>
  );
}
